import {
  Box, IconButton, InputAdornment, Link, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import TimerOffOutlinedIcon from '@mui/icons-material/TimerOffOutlined';

import type { Category, Poi } from '../api/types';

/**
 * How long to stay at each chosen place, one row per place.
 *
 * The same shape as the avoidance panel's per-place distances, and for the
 * same reason: one number rarely suits everything on a list. A cathedral and
 * a viewpoint are both "places to visit" and are not both twenty minutes.
 *
 * The field is left EMPTY when nothing has been chosen, showing the category's
 * default as a placeholder rather than as a value. That keeps the overrides
 * map small, makes clearing a field mean "back to the default" instead of
 * "zero minutes", and means a default changed in Admin takes effect without
 * anybody clearing a number they never typed.
 */
export function StayRow({
  label, hint, fallback, value, colour, onChange, onRemove, onOpen,
}: {
  label: string;
  hint: string;
  /** Shown as the placeholder — what this place gets if nothing is typed. */
  fallback: number;
  value: number | undefined;
  /** The colour this place is drawn in, so the row and the map agree. */
  colour?: string;
  onChange: (minutes: number | null) => void;
  /** Takes the place off the list. This row is the only place that can. */
  onRemove?: () => void;
  /** Opens the place's full record. Omit and the name is plain text. */
  onOpen?: () => void;
}) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      {colour && (
        <Box
          sx={{
            width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
            bgcolor: colour, border: '1px solid rgba(0,0,0,.15)',
          }}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/*
          The name opens the place, as it does everywhere else it appears.
          A row that names somewhere and cannot be asked about it is the one
          place in the app where a place name is inert.
        */}
        {onOpen ? (
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={onOpen}
            sx={{ display: 'block', maxWidth: '100%', textAlign: 'left', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {label}
          </Link>
        ) : (
          <Typography variant="body2" noWrap>{label}</Typography>
        )}
        <Typography
          variant="caption"
          noWrap
          sx={{ display: 'block' }}
          color={value === 0 ? 'primary.main' : 'text.secondary'}
        >
          {value === 0 ? 'Passes by \u2014 no stop' : hint}
        </Typography>
      </Box>

      {/*
        Going past without stopping, said as a choice rather than a number.

        Zero minutes has always meant this — the planner routes through the
        place and allows it no time — but it could only be asked for by
        typing 0 into a box labelled "min", which nobody does by accident or
        on purpose. The button says what the number means, and the box goes
        quiet while it is on, because a stay of zero is not a stay anybody
        is editing.
      */}
      <Tooltip title={value === 0
        ? `Stop at each ${label} for the usual time`
        : `Go past each ${label} without stopping`}
      >
        <IconButton
          size="small"
          color={value === 0 ? 'primary' : 'default'}
          aria-label={`Pass ${label} without stopping`}
          aria-pressed={value === 0}
          onClick={() => onChange(value === 0 ? null : 0)}
          sx={{ flexShrink: 0 }}
        >
          {value === 0
            ? <TimerOffOutlinedIcon fontSize="small" />
            : <TimerOutlinedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      <TextField
        size="small"
        type="number"
        disabled={value === 0}
        placeholder={String(fallback)}
        value={value ?? ''}
        onChange={(event) => {
          const raw = event.target.value.trim();
          if (raw === '') return onChange(null);
          const next = Number(raw);
          if (Number.isFinite(next)) onChange(Math.min(600, Math.max(0, Math.round(next))));
        }}
        slotProps={{
          htmlInput: { min: 0, max: 600, step: 5, 'aria-label': `Stay at ${label}` },
          input: { endAdornment: <InputAdornment position="end">min</InputAdornment> },
        }}
        sx={{ width: 108, flexShrink: 0 }}
      />
      {onRemove && (
        <Tooltip title={`Remove ${label}`}>
          <IconButton size="small" onClick={onRemove} sx={{ flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

/** A spot marked on the map, which has no category to take a stay time from. */
export interface StayPin {
  label?: string;
  dwellMinutes?: number;
}

/**
 * The rows for one "places to visit" list.
 *
 * This list IS the record of what has been chosen — the picker above keeps no
 * chips of its own, because saying it twice pushes the search field somebody
 * is typing into off its own control once a few places are on the list. So
 * removing a place happens here, on its row, beside its stay time.
 *
 * Renders nothing at all when nothing has been chosen: an empty heading over
 * an empty list is worse than no heading.
 */
export function StayRows({
  places, pins, categories, stopDwellMinutes, pinnedFallback, colourFor,
  onPlaceChange, onPinChange, onRemovePlace, onRemovePin, onShowDetails,
}: {
  places: Poi[];
  pins: StayPin[];
  categories: Category[];
  /** Overrides keyed by place id, as `PlanRequest.stopDwellMinutes` is. */
  stopDwellMinutes: Record<string, number>;
  /** What a marked spot gets when it is given no stay of its own. */
  pinnedFallback: number;
  colourFor?: (categoryKey: string) => string;
  onPlaceChange: (poiId: string, minutes: number | null) => void;
  onPinChange: (index: number, minutes: number | null) => void;
  /** Omit either and that kind of row offers no way off the list. */
  onRemovePlace?: (poiId: string) => void;
  onRemovePin?: (index: number) => void;
  /** Opens a place's full record. Marked spots have none, so they stay plain. */
  onShowDetails?: (poiId: string) => void;
}) {
  if (places.length === 0 && pins.length === 0) return null;

  const defaultFor = (poi: Poi) =>
    categories.find((c) => c.key === poi.categoryKey)?.defaultDwellMinutes ?? pinnedFallback;

  /*
   * Nothing chosen is nothing to show.
   *
   * The heading used to print above an empty list, so a round built from
   * kinds carried a "Chosen places" caption with no places under it. Hiding
   * here rather than at each call site lets callers render this whenever the
   * list COULD have something in it, without repeating the test.
   */
  if (places.length === 0 && pins.length === 0) return null;

  return (
    <Box sx={{ px: 0.5, pt: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        How long at each place? Blank keeps its usual time.
      </Typography>
      <Stack spacing={0.75}>
        {places.map((poi) => (
          <StayRow
            key={poi.id}
            label={poi.name}
            hint={`${poi.categoryLabel} · usually ${defaultFor(poi)} min`}
            fallback={defaultFor(poi)}
            value={stopDwellMinutes[poi.id]}
            {...(colourFor ? { colour: colourFor(poi.categoryKey) } : {})}
            onChange={(minutes) => onPlaceChange(poi.id, minutes)}
            {...(onRemovePlace ? { onRemove: () => onRemovePlace(poi.id) } : {})}
            {...(onShowDetails ? { onOpen: () => onShowDetails(poi.id) } : {})}
          />
        ))}
        {pins.map((pin, index) => (
          <StayRow
            // Pins have no id; their position in the list is what identifies
            // them, and removing one re-keys the rest, which is correct —
            // the row belongs to the spot at that position.
            key={`pin-${index}`}
            label={pin.label ?? `Marked spot ${index + 1}`}
            hint="a spot on the map, so there is no usual time to fall back on"
            fallback={pinnedFallback}
            value={pin.dwellMinutes}
            onChange={(minutes) => onPinChange(index, minutes)}
            {...(onRemovePin ? { onRemove: () => onRemovePin(index) } : {})}
          />
        ))}
      </Stack>
    </Box>
  );
}
